import type { MarketplaceSelectedFilters } from "@/types/api/marketplace";
import type {
  ProductSearchRequest,
  ProductTextFilter,
} from "@/types/api/marketplace-product";
import { toCheckboxFilters } from "./marketplace-url-sync";
import { MARKETPLACE_KEYWORDS_FILTER_TYPE } from "./transform-product-filters";

function mapSelectedIdsToTerms(
  selectedIds: number[],
  mapping: Record<number, string> | undefined
): string[] {
  if (!mapping || selectedIds.length === 0) return [];
  return selectedIds.map((id) => mapping[id]).filter(Boolean);
}

function buildFacetFilters(
  filters: MarketplaceSelectedFilters,
  filterMapping: Record<string, Record<number, string>>
): ProductSearchRequest["facetFilters"] {
  const checkboxFilters = toCheckboxFilters(filters);
  const facetFilters: NonNullable<ProductSearchRequest["facetFilters"]> = [];

  Object.entries(checkboxFilters).forEach(([filterName, selectedIds]) => {
    if (filterName === MARKETPLACE_KEYWORDS_FILTER_TYPE) return;
    if (selectedIds.length === 0 || !filterMapping[filterName]) return;

    const values = mapSelectedIdsToTerms(
      selectedIds,
      filterMapping[filterName]
    );

    if (values.length > 0) {
      facetFilters.push({ name: filterName, values });
    }
  });

  return facetFilters;
}

function buildKeywords(
  filters: MarketplaceSelectedFilters,
  filterMapping: Record<string, Record<number, string>>
): string[] {
  const selectedIds =
    toCheckboxFilters(filters)[MARKETPLACE_KEYWORDS_FILTER_TYPE] ?? [];
  return mapSelectedIdsToTerms(
    selectedIds,
    filterMapping[MARKETPLACE_KEYWORDS_FILTER_TYPE]
  );
}

function buildTextFilter(searchText: string): ProductTextFilter {
  return {
    text: searchText.trim(),
    operator: "OR",
    wildcard: true,
    searchTitle: true,
    searchFullText: true,
    searchExternalId: true,
    searchCode: true,
  };
}

export function buildProductSearchRequest(
  filters: MarketplaceSelectedFilters,
  filterMapping: Record<string, Record<number, string>>,
  searchText = ""
): ProductSearchRequest {
  const body: ProductSearchRequest = {};
  const facetFilters = buildFacetFilters(filters, filterMapping) ?? [];
  const keywords = buildKeywords(filters, filterMapping);

  if (facetFilters.length > 0) {
    body.facetFilters = facetFilters;
  }

  if (keywords.length > 0) {
    body.keywords = keywords;
  }

  if (searchText.trim()) {
    body.textFilter = buildTextFilter(searchText);
  }

  return body;
}
